import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated, isAdmin, isSuperAdmin, isConcoursStudent, isEducatorDemo, getUser, getTokenTimeRemaining } from './utils/auth';
import SplashScreen from './components/SplashScreen';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import StudentDashboard from './pages/StudentDashboard';
import ConcoursDashboard from './pages/ConcoursDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import EducatorDashboardPage from './pages/EducatorDashboardPage';
import DailyQuizPage from './pages/DailyQuizPage';
import QuizSelectionPage from './pages/QuizSelectionPage';
import GrammarPage from './pages/GrammarPage';
import InterviewPage from './pages/InterviewPage';
import SEOPage from './pages/SEOPage';
import PitchPage from './pages/PitchPage';
import TypingGamePage from './pages/TypingGamePage';
import PressHistoryPage from './pages/PressHistoryPage';
import MediaEconomicsPage from './pages/MediaEconomicsPage';
import WhoIsWhoPage from './pages/WhoIsWhoPage';
import StyleExpressionsPage from './pages/StyleExpressionsPage';
import HallOfFamePage from './pages/HallOfFamePage';
import JournalPage from './pages/JournalPage';
import JournalArticlePage from './pages/JournalArticlePage';
import StudentArticlesPage from './pages/StudentArticlesPage';
import ProfessorArticlesPage from './pages/ProfessorArticlesPage';
import MessagingPage from './pages/MessagingPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import OSINTPage from './pages/OSINTPage';
import LegalPage from './pages/LegalPage';
import RecommendPage from './pages/RecommendPage';
import CourseRequestPage from './pages/CourseRequestPage';
import QuizSourcesPage from './pages/QuizSourcesPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ProspectionAdminPage from './pages/ProspectionAdminPage';
import BreathingBreak from './components/BreathingBreak';
import './App.css';

// Intervalle de vérification de la session (5 minutes)
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000;

// Intervalle de pause "respiration" (10 minutes)
const BREATHING_BREAK_INTERVAL = 10 * 60 * 1000;

// Pages d'exercices qui déclenchent la pause respiration
const EXERCISE_PATHS = ['/quiz', '/grammar', '/interview', '/seo', '/typing', '/osint', '/press-history', '/media-economics', '/who-is-who', '/pitch'];

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false, educatorOnly = false }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (superAdminOnly && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Wrapper avec pause respiration pour les pages d'exercices
function ExerciseWrapper({ children }) {
  const currentPath = window.location.pathname;
  const isExercisePage = EXERCISE_PATHS.some(path => currentPath.startsWith(path));
  
  if (isExercisePage) {
    return (
      <BreathingBreak 
        breakInterval={BREATHING_BREAK_INTERVAL}
        enabled={true}
      >
        {children}
      </BreathingBreak>
    );
  }
  
  return children;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }) {
  if (isAuthenticated()) {
    const user = getUser();
    if (isSuperAdmin()) {
      return <Navigate to="/superadmin" replace />;
    }
    if (isConcoursStudent()) {
      return <Navigate to="/concours-dashboard" replace />;
    }
    return <Navigate to={isAdmin() ? '/admin' : '/dashboard'} replace />;
  }
  return children;
}

function App() {
  const [showSplash, setShowSplash] = useState(false);
  const [initialPath] = useState(window.location.pathname);

  // Vérification périodique de la validité de la session
  const checkSession = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token && !isAuthenticated()) {
      // La session a expiré pendant que l'utilisateur était inactif
      console.warn('Session expirée pendant l\'inactivité');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('hasSeenSplash');
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/journal')) {
        window.location.replace('/login');
      }
    }
  }, []);

  useEffect(() => {
    // Vérification initiale
    checkSession();
    
    // Vérification périodique toutes les 5 minutes
    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);
    
    // Vérification lors du retour de l'utilisateur sur l'onglet
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };
    
    // Vérification lors du focus de la fenêtre (retour après alt+tab)
    const handleFocus = () => {
      checkSession();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkSession]);

  useEffect(() => {
    // Splash screen TOUJOURS sur la page d'accueil (landing page)
    const isLandingPage = initialPath === '/' || initialPath === '/landing';
    
    if (isLandingPage) {
      setShowSplash(true);
    }
  }, [initialPath]);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // Easter egg: allow replaying splash from anywhere
  const triggerSplash = () => {
    sessionStorage.removeItem('hasSeenSplash');
    setShowSplash(true);
  };

  // Expose triggerSplash globally for the AppHeader to use
  useEffect(() => {
    window.triggerSplash = triggerSplash;
    return () => { delete window.triggerSplash; };
  }, []);

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <BrowserRouter>
      <ExerciseWrapper>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } />
        
        {/* Student Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/quiz" element={
          <ProtectedRoute>
            <DailyQuizPage />
          </ProtectedRoute>
        } />
        <Route path="/quiz-selection" element={
          <ProtectedRoute>
            <QuizSelectionPage />
          </ProtectedRoute>
        } />
        <Route path="/grammar" element={
          <ProtectedRoute>
            <GrammarPage />
          </ProtectedRoute>
        } />
        <Route path="/interview" element={
          <ProtectedRoute>
            <InterviewPage />
          </ProtectedRoute>
        } />
        <Route path="/seo" element={
          <ProtectedRoute>
            <SEOPage />
          </ProtectedRoute>
        } />
        <Route path="/pitch" element={
          <ProtectedRoute>
            <PitchPage />
          </ProtectedRoute>
        } />
        <Route path="/osint" element={
          <ProtectedRoute>
            <OSINTPage />
          </ProtectedRoute>
        } />
        <Route path="/typing" element={
          <ProtectedRoute>
            <TypingGamePage />
          </ProtectedRoute>
        } />
        <Route path="/press-history" element={
          <ProtectedRoute>
            <PressHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/media-economics" element={
          <ProtectedRoute>
            <MediaEconomicsPage />
          </ProtectedRoute>
        } />
        <Route path="/who-is-who" element={
          <ProtectedRoute>
            <WhoIsWhoPage />
          </ProtectedRoute>
        } />
        <Route path="/style" element={
          <ProtectedRoute>
            <StyleExpressionsPage />
          </ProtectedRoute>
        } />
        <Route path="/hall-of-fame" element={
          <ProtectedRoute>
            <HallOfFamePage />
          </ProtectedRoute>
        } />
        
        {/* Educator Dashboard */}
        <Route path="/educator-dashboard" element={
          <ProtectedRoute>
            <EducatorDashboardPage />
          </ProtectedRoute>
        } />
        
        {/* Professor Articles Management */}
        <Route path="/professor/articles" element={
          <ProtectedRoute>
            <ProfessorArticlesPage />
          </ProtectedRoute>
        } />
        
        {/* Student Articles */}
        <Route path="/student/articles" element={
          <ProtectedRoute>
            <StudentArticlesPage />
          </ProtectedRoute>
        } />
        
        {/* Public Journal (no auth required) */}
        <Route path="/journal/:schoolSlug/:classSlug/:year" element={<JournalPage />} />
        <Route path="/journal/:schoolSlug/:classSlug/:year/:slug" element={<JournalArticlePage />} />
        
        {/* Concours Student Dashboard */}
        <Route path="/concours-dashboard" element={
          <ProtectedRoute>
            <ConcoursDashboard />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        {/* Super Admin Routes */}
        <Route path="/superadmin" element={
          <ProtectedRoute superAdminOnly={true}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        
        {/* Messaging Page */}
        <Route path="/messaging" element={
          <ProtectedRoute>
            <MessagingPage />
          </ProtectedRoute>
        } />
        
        {/* Analytics Dashboard */}
        <Route path="/analytics" element={
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } />
        
        {/* Legal Pages (Public) */}
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/mentions-legales" element={<LegalPage />} />
        <Route path="/cgu" element={<LegalPage />} />
        <Route path="/cgv" element={<LegalPage />} />
        
        {/* Recommandation */}
        <Route path="/recommend" element={<RecommendPage />} />
        
        {/* Director/Admin Pages */}
        <Route path="/director/course-requests" element={
          <ProtectedRoute educatorOnly={true}><CourseRequestPage /></ProtectedRoute>
        } />
        <Route path="/director/sources" element={
          <ProtectedRoute educatorOnly={true}><QuizSourcesPage /></ProtectedRoute>
        } />
        <Route path="/director/subscription" element={
          <ProtectedRoute educatorOnly={true}><SubscriptionPage /></ProtectedRoute>
        } />
        
        {/* Payment Success */}
        <Route path="/payment/success" element={
          <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>
        } />
        
        {/* Admin Prospection - Super Admin only */}
        <Route path="/admin/prospection" element={
          <ProtectedRoute><ProspectionAdminPage /></ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ExerciseWrapper>
    </BrowserRouter>
  );
}

export default App;
